/**
 * Which menu types a property owner may use, and what follows from the set
 * (PRD OPS-013).
 *
 * Operations ticks the menu types a client is sold; the owner assigns one of
 * the ticked types to each apartment. Everything else is a consequence, and
 * every consequence is stated HERE so the worker and both portals cannot each
 * decide it differently:
 *
 * - one type ticked: the owner has no choice, the portal offers no control,
 *   and every apartment is on that type;
 * - several ticked: the owner picks per apartment, among the ticked ones;
 * - the set shrinks: an apartment on a type that is no longer ticked moves to
 *   the first ticked type. With ONE type left that is every apartment, which
 *   is the "reset" - it is not a second rule.
 *
 * Nothing in the ticked-set rules below names 'A' or 'B' except the fallback
 * for a client nobody has configured. Adding B2 (founder, 2026-09-20) was
 * exactly that: a new member of `StorefrontMode` and a label per portal, with
 * the stored shape (a list) and the checkboxes (drawn from `STOREFRONT_MODES`)
 * untouched.
 *
 * What DOES name the modes is the second half of this file: the three questions
 * every surface used to answer for itself with a `=== 'B'`. Who chooses the
 * shop, who may be named, and whether there is a second shop anywhere.
 */
import { z } from 'zod';
import { StorefrontMode } from '../enums';

/** Every menu type, in the order every surface lists them. */
export const STOREFRONT_MODES: readonly StorefrontMode[] = StorefrontMode.options;

/**
 * DOES THE GUEST PICK THE SHOP? (GST-030, founder decision 2026-09-20.)
 *
 * True for `B` alone. `A` allocates, and `B2` picks the closest mode B shop at
 * every scan - so neither has a chooser, `GET /g/shops` means nothing to
 * either, and a `shopId` either of them sends is not a choice the platform
 * made.
 *
 * Exhaustive by construction: a fourth mode is a typecheck failure here rather
 * than a silent `false` from a `=== 'B'` somebody wrote in a route.
 */
const GUEST_CHOOSES_SHOP: Record<StorefrontMode, boolean> = { A: false, B: true, B2: false };

/**
 * IS THE SHOP NAMED TO THE GUEST? (CAT-009, GST-029, GST-030.)
 *
 * True for `B` alone, and it is a different question from the one above even
 * though the answers coincide today. `A` hides the house because the guest
 * never chose it; `B2` hides it because the founder's decision says the guest
 * is never shown which bakery bakes their bag - not the name, the logo, the
 * hero image, the description or the house behind it. The unnamed pickup POINT
 * is not this question (founder, 2026-09-08), and neither is the shop's own
 * weekly calendar.
 */
const NAMES_THE_SHOP: Record<StorefrontMode, boolean> = { A: false, B: true, B2: false };

/**
 * IS THIS ORDER BOUND TO ONE SHOP, WITH NO SECOND ANYWHERE? (Founder decision
 * 2026-09-20.)
 *
 * True for `B2` alone. The closest shop is re-computed at every SCAN and held
 * for that session, and if it refuses or never answers the order ends - there
 * is no cascade to another shop, not even to a sibling counter of the same
 * house, because the guest was never offered a house to fall back inside.
 *
 * Mode B falls back within the house the guest chose (ALC-010); mode A cascades
 * across the whole pool (ALC-004). This is the third answer, and it is "nowhere".
 */
const BOUND_TO_ONE_SHOP: Record<StorefrontMode, boolean> = { A: false, B: false, B2: true };

/** Does this mode show the guest a chooser of named houses? */
export function guestChoosesShop(mode: StorefrontMode): boolean {
  return GUEST_CHOOSES_SHOP[mode];
}

/** May this mode put a shop's identity - name, logo, imagery - on the wire? */
export function namesTheShop(mode: StorefrontMode): boolean {
  return NAMES_THE_SHOP[mode];
}

/** Is there exactly one shop that may ever serve this order, and no fallback? */
export function boundToOneShop(mode: StorefrontMode): boolean {
  return BOUND_TO_ONE_SHOP[mode];
}

/**
 * Does the platform pick from the WHOLE pool, with runners-up behind it
 * (ALC-001, ALC-004)?
 *
 * The third answer to "who picks the shop", derived from the other two rather
 * than tabled again - so it cannot drift from them, and a fourth mode still
 * fails the typecheck in exactly one place. True for `A` alone today: the guest
 * picks in `B`, and in `B2` the platform picks ONE shop and keeps no runner-up.
 */
export function platformAllocatesFreely(mode: StorefrontMode): boolean {
  return !guestChoosesShop(mode) && !boundToOneShop(mode);
}

/**
 * What a client nobody has configured is on: mode B2, alone (founder,
 * 2026-09-23 - every new client starts on B2; it was mode A until then). A
 * client whose mode operations has saved keeps what was saved. This is what a
 * client with no stored row reads - every client as it is created - and the
 * mode its first apartment's code is minted on.
 */
export const UNCONFIGURED_STOREFRONT_MODES: readonly StorefrontMode[] = ['B2'];

/**
 * The wire shape of the ticked set: at least one type, none twice. Order is
 * not meaning - `normalizeStorefrontModes` puts any accepted set in the
 * canonical order - so a client may send the ticks in the order they were made.
 */
export const AllowedStorefrontModes = z
  .array(StorefrontMode)
  .min(1)
  .max(StorefrontMode.options.length)
  .refine((modes) => new Set(modes).size === modes.length, {
    message: 'A menu type is listed once',
  });
export type AllowedStorefrontModes = z.infer<typeof AllowedStorefrontModes>;

/**
 * What a SAVE may name: exactly one mode (founder, 2026-09-21 - "only one mode
 * at a time is possible"). A client is on one mode and every apartment follows
 * it, so the owner is never asked to choose.
 *
 * A REQUEST shape only, and deliberately not the shape above. Clients were
 * saved while several types could be ticked, those rows are still stored, and
 * `AllowedStorefrontModes` is what every RESPONSE carrying them validates
 * against - tighten that one and each such client answers 500 on its own
 * page. So the rule is held where a new value enters, and a stored set of two
 * stays readable until somebody saves that client again.
 */
export const ChosenStorefrontMode = AllowedStorefrontModes.refine((modes) => modes.length === 1, {
  message: 'A client is on one mode',
});

/**
 * Any list of candidate values as a valid ticked set: known types only, each
 * once, in canonical order, and never empty. Tolerant on purpose - it is what
 * reads a STORED value, and a stored value outlives the code that wrote it.
 */
export function normalizeStorefrontModes(
  candidates: readonly unknown[],
): readonly StorefrontMode[] {
  const kept = STOREFRONT_MODES.filter((mode) => candidates.includes(mode));
  return kept.length > 0 ? kept : UNCONFIGURED_STOREFRONT_MODES;
}

/** The type a new apartment takes when its creator names none: the first ticked. */
export function defaultStorefrontModeOf(allowed: readonly StorefrontMode[]): StorefrontMode {
  return allowed[0] ?? (UNCONFIGURED_STOREFRONT_MODES[0] as StorefrontMode);
}

/** Does the owner get a control at all? Only with more than one type to pick from. */
export function ownerChoosesStorefrontMode(allowed: readonly StorefrontMode[]): boolean {
  return allowed.length > 1;
}

/** May an apartment of this client be put on `mode`? */
export function storefrontModeAllowed(
  allowed: readonly StorefrontMode[],
  mode: StorefrontMode,
): boolean {
  return allowed.includes(mode);
}

/**
 * The type an apartment is on once the ticked set is `allowed`: unchanged when
 * its type is still ticked, the first ticked type otherwise.
 */
export function storefrontModeUnder(
  allowed: readonly StorefrontMode[],
  current: StorefrontMode,
): StorefrontMode {
  return storefrontModeAllowed(allowed, current) ? current : defaultStorefrontModeOf(allowed);
}
